<script lang="ts">
  import OutlineView from './OutlineView.svelte'
  import { detectSchema } from './index'

  type Entry = {
    key: string
    value: unknown
  }

  type ParsedState =
    | {
        ok: true
        value: unknown
      }
    | {
        ok: false
        error: string
      }

  let {
    value,
    label = 'JSON',
    depth = 0,
  }: {
    value: unknown
    label?: string
    depth?: number
  } = $props()

  const parsedState = $derived(parseValue(value))
  const nodeValue = $derived(parsedState.ok ? parsedState.value : undefined)
  const containerValue = $derived(isContainer(nodeValue) ? nodeValue : undefined)
  const isContainerNode = $derived(Boolean(containerValue))
  const entries = $derived(containerValue ? getEntries(containerValue) : [])
  const collapsedByDefault = $derived(
    depth === 0 &&
      ((Array.isArray(nodeValue) && entries.length > 5) ||
        (isPlainObject(nodeValue) && entries.length > 8)),
  )
  const schema = $derived(parsedState.ok ? detectSchema(parsedState.value) : undefined)
  let isCollapsed = $state(false)

  $effect(() => {
    isCollapsed = collapsedByDefault
  })

  function parseValue(input: unknown): ParsedState {
    if (typeof input !== 'string' || depth > 0) {
      return { ok: true, value: input }
    }

    try {
      const parsed = JSON.parse(input)
      detectSchema(parsed, input)
      return { ok: true, value: parsed }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Unable to parse JSON',
      }
    }
  }

  function isPlainObject(input: unknown): input is Record<string, unknown> {
    return typeof input === 'object' && input !== null && !Array.isArray(input)
  }

  function isContainer(input: unknown): input is Record<string, unknown> | unknown[] {
    return isPlainObject(input) || Array.isArray(input)
  }

  function getEntries(input: Record<string, unknown> | unknown[]): Entry[] {
    if (Array.isArray(input)) {
      return input.map((item, index) => ({ key: `[${index}]`, value: item }))
    }

    return Object.entries(input).map(([key, entryValue]) => ({ key, value: entryValue }))
  }

  function getContainerSummary(input: Record<string, unknown> | unknown[]): string {
    if (Array.isArray(input)) {
      return `Array ${input.length} ${input.length === 1 ? 'item' : 'items'}`
    }

    const keyCount = Object.keys(input).length
    return `Object ${keyCount} ${keyCount === 1 ? 'key' : 'keys'}`
  }

  function isDateString(input: string): boolean {
    return /^\d{4}-\d{2}-\d{2}/.test(input)
  }

  function isEmailString(input: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
  }

  function isUrlString(input: string): boolean {
    return /^https?:\/\//i.test(input)
  }

  function getPrimitiveClass(input: unknown): string {
    if (typeof input === 'number') {
      return 'outline-value outline-value-number'
    }

    if (typeof input === 'boolean') {
      return input ? 'outline-value outline-value-boolean-true' : 'outline-value outline-value-boolean-false'
    }

    if (input === null) {
      return 'outline-value outline-value-null'
    }

    if (typeof input === 'string') {
      if (isEmailString(input) || isUrlString(input)) {
        return 'outline-value outline-value-link'
      }

      if (isDateString(input)) {
        return 'outline-value outline-value-date'
      }
    }

    return 'outline-value'
  }

  function formatPrimitive(input: unknown): string {
    if (input === null) {
      return 'Not provided'
    }

    if (typeof input === 'string') {
      return input
    }

    return String(input)
  }
</script>

{#if !parsedState.ok}
  <div class="reader-outline" style={`--outline-depth: ${depth}`}>
    <div class="outline-row outline-error-row">
      <span class="outline-key">Invalid JSON</span>
      <span class="outline-value outline-value-null">{parsedState.error}</span>
    </div>
  </div>
{:else}
  <div class="reader-outline" data-schema-kind={schema?.kind} style={`--outline-depth: ${depth}`}>
    <div class:outline-section-collapsed={isContainerNode && isCollapsed}>
      <div class="outline-row">
        {#if isContainerNode}
          <button
            type="button"
            class="outline-toggle"
            aria-label={isCollapsed ? `Expand ${label}` : `Collapse ${label}`}
            aria-expanded={!isCollapsed}
            onclick={() => {
              isCollapsed = !isCollapsed
            }}
          >
            {isCollapsed ? '›' : '⌄'}
          </button>
          <span class="outline-key">{label}</span>
          <span class="outline-value outline-value-summary">
            {containerValue ? getContainerSummary(containerValue) : ''}
          </span>
        {:else}
          <span class="outline-toggle outline-toggle-spacer" aria-hidden="true"></span>
          <span class="outline-key">{label}</span>
          {#if typeof nodeValue === 'string' && isEmailString(nodeValue)}
            <a class={getPrimitiveClass(nodeValue)} href={`mailto:${nodeValue}`}>{nodeValue}</a>
          {:else if typeof nodeValue === 'string' && isUrlString(nodeValue)}
            <a class={getPrimitiveClass(nodeValue)} href={nodeValue} target="_blank" rel="noreferrer">
              {nodeValue}
            </a>
          {:else}
            <span class={getPrimitiveClass(nodeValue)}>{formatPrimitive(nodeValue)}</span>
          {/if}
        {/if}
      </div>

      {#if isContainerNode && !isCollapsed}
        <div class="outline-children">
          {#each entries as entry (entry.key)}
            <OutlineView value={entry.value} label={entry.key} depth={depth + 1} />
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}
